/**
 * FL-118: 日次レポート生成サービス
 * 案件一覧・陸送員スケジュールのCSV・PDF生成機能
 */

import { format, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';
import { formatDateForCSV, formatCurrencyForCSV } from '../utils/csvExport';
import { generateCasePDF } from '../utils/pdfGenerator';
import { getSupabaseClient } from '../utils/supabase';
import logger from '../utils/logger';
import type {
  Case,
  LandTransporter,
  DailyReportData,
  EmailAttachment,
  ReportFileFormat,
} from '../types';

export class ReportGeneratorService {
  /**
   * 指定された会社・期間のレポートデータを取得
   */
  async getReportData(companyId: string, startDate: Date, endDate: Date): Promise<DailyReportData> {
    const startTime = performance.now();

    try {
      const supabase = getSupabaseClient();
      if (!supabase) throw new Error('Supabaseクライアントが利用できません');

      // 会社情報取得
      const { data: company } = await supabase
        .from('companies')
        .select('id, name, contact_person, phone, email')
        .eq('id', companyId)
        .single();

      if (!company) {
        throw new Error(`Company not found: ${companyId}`);
      }

      // 案件データ取得
      const { data: cases } = await supabase
        .from('company_cases')
        .select(
          `
          *,
          pickup_partner:business_partners!pickup_partner_id(name, display_name),
          delivery_partner:business_partners!delivery_partner_id(name, display_name),
          billing_partner:business_partners!billing_partner_id(name, display_name),
          assigned_transporter:company_land_transporters(name, phone)
        `
        )
        .eq('company_id', companyId)
        .gte('pickup_date', format(startDate, 'yyyy-MM-dd'))
        .lte('pickup_date', format(endDate, 'yyyy-MM-dd'))
        .order('pickup_date', { ascending: true });

      // 陸送員データ取得
      const { data: drivers } = await supabase
        .from('company_land_transporters')
        .select('*')
        .eq('company_id', companyId)
        .eq('status', 'available')
        .order('name', { ascending: true });

      // スケジュールデータは削除済み（company_transporter_schedulesテーブル削除のため）

      // データ集計
      const casesData = cases || [];
      const driversData = drivers || [];
      const completedCases = casesData.filter(c => c.status === 'completed').length;

      const reportData: DailyReportData = {
        companyInfo: {
          id: company.id,
          name: company.name,
          contactPerson: company.contact_person,
          phone: company.phone,
          email: company.email,
        },
        reportDate: format(new Date(), 'yyyy-MM-dd'),
        dateRange: {
          start: format(startDate, 'yyyy-MM-dd'),
          end: format(endDate, 'yyyy-MM-dd'),
        },
        cases: casesData as Case[],
        drivers: driversData as LandTransporter[],
        summary: {
          totalCases: casesData.length,
          totalDrivers: driversData.length,
          completedCases,
          pendingCases: casesData.length - completedCases,
        },
      };

      const endTime = performance.now();
      logger.info('レポートデータ取得完了', {
        companyId,
        dateRange: reportData.dateRange,
        casesCount: casesData.length,
        driversCount: driversData.length,
        executionTimeMs: Math.round(endTime - startTime),
      });

      return reportData;
    } catch (error) {
      logger.error('レポートデータ取得エラー', { companyId, error });
      throw error;
    }
  }

  /**
   * 案件一覧CSVを生成
   */
  generateCaseListCSV(data: DailyReportData): Buffer {
    const headers = [
      '案件番号',
      '状態',
      '引取先',
      '引取住所',
      '引取日',
      '引取時間（開始）',
      '引取時間（終了）',
      '搬入先',
      '搬入住所',
      '搬入日',
      '搬入時間',
      '車両番号',
      '車種',
      '担当陸送員',
      '陸送費',
      'ステータス',
      '備考',
    ];

    const rows = data.cases.map(case_ => [
      case_.caseNumber || '',
      this.getStatusDisplayName(case_.status),
      this.getPartnerDisplayName((case_ as any).pickup_partner),
      case_.pickupAddress || '',
      formatDateForCSV(case_.pickupDate),
      case_.pickupTimeStart || '',
      case_.pickupTimeEnd || '',
      this.getPartnerDisplayName((case_ as any).delivery_partner),
      case_.deliveryAddress || '',
      formatDateForCSV(case_.deliveryDate),
      case_.deliveryTime || '',
      case_.vehiclePlateNumber || '',
      case_.vehicleModel || '',
      case_.assignedLandTransporterId || '',
      case_.transportFee ? formatCurrencyForCSV(case_.transportFee) : '',
      this.getStatusDisplayName(case_.status),
      case_.remarks || '',
    ]);

    // CSV文字列を生成
    const csvContent = [
      headers.join(','),
      ...rows.map(row =>
        row
          .map(cell => {
            if (
              typeof cell === 'string' &&
              (cell.includes(',') || cell.includes('\n') || cell.includes('"'))
            ) {
              return `"${cell.replace(/"/g, '""')}"`;
            }
            return cell;
          })
          .join(',')
      ),
    ].join('\n');

    // BOM付きでBufferを作成
    const BOM = '\uFEFF';
    return Buffer.from(BOM + csvContent, 'utf8');
  }

  /**
   * 陸送員スケジュールCSVを生成
   */
  generateDriverScheduleCSV(data: DailyReportData): Buffer {
    const headers = [
      '陸送員名',
      '電話番号',
      '住所',
      '稼働状態',
      '運転可能車種',
      '大型免許',
      '夜間対応',
      '長距離対応',
      '仮ナンバー対応',
      'AT専用',
    ];

    const rows = data.drivers.map(driver => [
      driver.name,
      driver.phone,
      driver.address || '',
      this.getDriverStatusDisplayName(driver.status),
      (driver.drivableVehicleTypes || []).join('・'),
      driver.hasLicenseForLargeVehicles ? '○' : '×',
      driver.canWorkNightShift ? '○' : '×',
      driver.canWorkLongDistance ? '○' : '×',
      driver.hasTemporaryLicense ? '○' : '×',
      driver.isATOnly ? '○' : '×',
    ]);

    // CSV文字列を生成
    const csvContent = [
      headers.join(','),
      ...rows.map(row =>
        row
          .map(cell => {
            if (
              typeof cell === 'string' &&
              (cell.includes(',') || cell.includes('\n') || cell.includes('"'))
            ) {
              return `"${cell.replace(/"/g, '""')}"`;
            }
            return cell;
          })
          .join(',')
      ),
    ].join('\n');

    // BOM付きでBufferを作成
    const BOM = '\uFEFF';
    return Buffer.from(BOM + csvContent, 'utf8');
  }

  /**
   * 業務継続用PDFレポートを生成
   */
  async generateCombinedPDF(data: DailyReportData): Promise<Buffer> {
    try {
      // HTML要素を動的に作成
      const element = document.createElement('div');
      element.className = 'daily-report-container';
      element.innerHTML = this.generatePDFContent(data);

      // スタイルを追加
      const style = document.createElement('style');
      style.textContent = this.getPDFStyles();
      document.head.appendChild(style);

      // 一時的にDOMに追加
      document.body.appendChild(element);

      try {
        // PDF生成
        const result = await generateCasePDF(element, 'table', {
          filename: `業務継続用レポート_${data.reportDate}.pdf`,
          orientation: 'portrait',
          format: 'a4',
        });

        if (!result.success || !result.filename) {
          throw new Error(result.error || 'PDF生成に失敗しました');
        }

        // TODO: PDF生成機能の完全実装が必要
        // 現在のgenerateCasePDFはダウンロード専用のため、バイナリデータを返さない
        // 将来的にはサーバーサイドPDF生成（Puppeteer等）の実装を検討
        const pdfBuffer = Buffer.from('PDF placeholder'); // 一時的な実装

        return pdfBuffer;
      } finally {
        // クリーンアップ
        document.body.removeChild(element);
        document.head.removeChild(style);
      }
    } catch (error) {
      logger.error('PDF生成エラー', error);
      throw error;
    }
  }

  /**
   * 指定された形式でレポートファイルを生成
   */
  async generateReportFiles(
    data: DailyReportData,
    formats: ReportFileFormat[]
  ): Promise<EmailAttachment[]> {
    const attachments: EmailAttachment[] = [];
    const startTime = performance.now();

    try {
      for (const format of formats) {
        if (format === 'csv') {
          // 案件一覧CSV
          const casesCsv = this.generateCaseListCSV(data);
          attachments.push({
            filename: `案件一覧_${data.reportDate}.csv`,
            content: casesCsv,
            contentType: 'text/csv',
            size: casesCsv.length,
          });

          // 陸送員スケジュールCSV
          const driversCsv = this.generateDriverScheduleCSV(data);
          attachments.push({
            filename: `陸送員スケジュール_${data.reportDate}.csv`,
            content: driversCsv,
            contentType: 'text/csv',
            size: driversCsv.length,
          });
        } else if (format === 'pdf') {
          // 統合PDFレポート
          const pdf = await this.generateCombinedPDF(data);
          attachments.push({
            filename: `業務継続用レポート_${data.reportDate}.pdf`,
            content: pdf,
            contentType: 'application/pdf',
            size: pdf.length,
          });
        }
      }

      const endTime = performance.now();
      logger.info('レポートファイル生成完了', {
        formats,
        filesCount: attachments.length,
        totalSize: attachments.reduce((sum, att) => sum + att.size, 0),
        executionTimeMs: Math.round(endTime - startTime),
      });

      return attachments;
    } catch (error) {
      logger.error('レポートファイル生成エラー', { formats, error });
      throw error;
    }
  }

  private generatePDFContent(data: DailyReportData): string {
    return `
      <div class="report-header">
        <h1>${data.companyInfo.name} 業務継続用レポート</h1>
        <div class="report-meta">
          <p>レポート日: ${format(new Date(), 'yyyy年MM月dd日', { locale: ja })}</p>
          <p>対象期間: ${format(parseISO(data.dateRange.start), 'MM/dd', { locale: ja })} 〜 ${format(parseISO(data.dateRange.end), 'MM/dd', { locale: ja })}</p>
          <p>緊急連絡先: ${data.companyInfo.phone || '未設定'}</p>
        </div>
      </div>

      <div class="summary-section">
        <h2>概要</h2>
        <div class="summary-grid">
          <div class="summary-item">
            <span class="summary-label">総案件数</span>
            <span class="summary-value">${data.summary.totalCases}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">完了案件</span>
            <span class="summary-value">${data.summary.completedCases}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">進行中案件</span>
            <span class="summary-value">${data.summary.pendingCases}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">陸送員数</span>
            <span class="summary-value">${data.summary.totalDrivers}</span>
          </div>
        </div>
      </div>

      <div class="cases-section">
        <h2>案件一覧</h2>
        <table class="cases-table">
          <thead>
            <tr>
              <th>案件番号</th>
              <th>引取先</th>
              <th>搬入先</th>
              <th>引取日</th>
              <th>担当陸送員</th>
              <th>状態</th>
              <th>手動メモ</th>
            </tr>
          </thead>
          <tbody>
            ${data.cases
              .map(
                case_ => `
              <tr>
                <td>${case_.caseNumber || ''}</td>
                <td>${this.getPartnerDisplayName((case_ as any).pickup_partner)}</td>
                <td>${this.getPartnerDisplayName((case_ as any).delivery_partner)}</td>
                <td>${formatDateForCSV(case_.pickupDate)}</td>
                <td>${case_.assignedLandTransporterId || ''}</td>
                <td>${this.getStatusDisplayName(case_.status)}</td>
                <td class="memo-cell"></td>
              </tr>
            `
              )
              .join('')}
          </tbody>
        </table>
      </div>

      <div class="drivers-section">
        <h2>陸送員出勤予定</h2>
        <table class="drivers-table">
          <thead>
            <tr>
              <th>陸送員名</th>
              <th>電話番号</th>
              <th>稼働状態</th>
              <th>運転可能車種</th>
              <th>配車予定</th>
              <th>手動メモ</th>
            </tr>
          </thead>
          <tbody>
            ${data.drivers
              .map(
                driver => `
              <tr>
                <td>${driver.name}</td>
                <td>${driver.phone}</td>
                <td>${this.getDriverStatusDisplayName(driver.status)}</td>
                <td>${(driver.drivableVehicleTypes || []).join('・')}</td>
                <td class="assignment-cell"></td>
                <td class="memo-cell"></td>
              </tr>
            `
              )
              .join('')}
          </tbody>
        </table>
      </div>

      <div class="emergency-section">
        <h2>【システム障害時の対応手順】</h2>
        <ol>
          <li>印刷したこのレポートを基に手動配車を実施</li>
          <li>「手動メモ」欄に配車状況・変更点を記入</li>
          <li>システム復旧後、手動変更内容をシステムに反映</li>
          <li>緊急連絡先: ${data.companyInfo.phone || '未設定'}</li>
        </ol>
      </div>
    `;
  }

  private getPDFStyles(): string {
    return `
      @page {
        size: A4;
        margin: 15mm;
      }
      
      .daily-report-container {
        font-family: 'Noto Sans JP', sans-serif;
        font-size: 9pt;
        line-height: 1.4;
      }
      
      .report-header {
        text-align: center;
        margin-bottom: 20px;
        border-bottom: 2px solid #333;
        padding-bottom: 10px;
      }
      
      .report-header h1 {
        font-size: 16pt;
        margin: 0 0 10px 0;
      }
      
      .report-meta p {
        margin: 2px 0;
        font-size: 8pt;
      }
      
      .summary-section {
        margin-bottom: 20px;
      }
      
      .summary-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 10px;
        margin-top: 10px;
      }
      
      .summary-item {
        border: 1px solid #ccc;
        padding: 8px;
        text-align: center;
      }
      
      .summary-label {
        display: block;
        font-size: 7pt;
        color: #666;
      }
      
      .summary-value {
        display: block;
        font-size: 12pt;
        font-weight: bold;
        margin-top: 2px;
      }
      
      .cases-section, .drivers-section {
        margin-bottom: 25px;
        page-break-inside: avoid;
      }
      
      h2 {
        font-size: 12pt;
        margin: 0 0 10px 0;
        padding: 5px 10px;
        background: #f0f0f0;
        border-left: 4px solid #333;
      }
      
      table {
        width: 100%;
        border-collapse: collapse;
        font-size: 7pt;
      }
      
      th, td {
        border: 1px solid #ccc;
        padding: 4px;
        text-align: left;
      }
      
      th {
        background: #f8f8f8;
        font-weight: bold;
        font-size: 7pt;
      }
      
      .memo-cell, .assignment-cell {
        min-height: 15px;
        background: #f9f9f9;
        border: 2px dashed #ccc;
      }
      
      .emergency-section {
        margin-top: 30px;
        padding: 15px;
        border: 2px solid #d32f2f;
        background: #fff3f3;
      }
      
      .emergency-section h2 {
        color: #d32f2f;
        background: transparent;
        border: none;
        font-size: 11pt;
      }
      
      .emergency-section ol {
        margin: 10px 0;
      }
      
      .emergency-section li {
        margin: 5px 0;
        font-size: 8pt;
      }
      
      @media print {
        body { 
          margin: 0; 
          font-size: 8pt; 
        }
        .daily-report-container {
          margin: 0;
        }
      }
    `;
  }

  private getPartnerDisplayName(partner: any): string {
    if (!partner) return '';
    return partner.displayName || partner.name || '';
  }

  private getStatusDisplayName(status: string): string {
    const statusMap: Record<string, string> = {
      draft: '下書き',
      unassigned: '未配車',
      assigned: '配車済み',
      in_transit: '配送中',
      completed: '完了',
      cancelled: 'キャンセル',
    };
    return statusMap[status] || status;
  }

  private getDriverStatusDisplayName(status: string): string {
    const statusMap: Record<string, string> = {
      available: '稼働中',
      busy: '配送中',
      unavailable: '休憩中',
      inactive: '非稼働',
    };
    return statusMap[status] || status;
  }
}

// シングルトンインスタンス
export const reportGeneratorService = new ReportGeneratorService();
